import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import MenuItem from "../MenuItem";
import SpiceLevel from '../SpiceLevel';
import { getTenantId } from "../../config/tenantConfig";
import apiCache from "../../utils/apiCache";
import "./index.css";

// Reused by every per-template component below: an item click should land
// the customer on the Restaurant Menu App already scoped to the category
// that item came from, not a generic "All Items" view. This reuses the
// exact same sessionStorage handoff CategoryGrid's category tiles already
// rely on (RestaurantMenuApp reads 'pending_category_id' on mount) — no new
// mechanism, just applying the existing one to item clicks here too.
const goToItemCategory = (navigate, categoryId) => {
  if (categoryId != null) sessionStorage.setItem('pending_category_id', String(categoryId));
  navigate('/restaurants');
};

// Distinct from any real branchId (undefined, number, or string) so "not
// fetched yet" can never be mistaken for "already fetched with branchId
// undefined" below.
const MENU_SECTION_NOT_FETCHED = Symbol('menu-section-not-fetched');

// Same guard RestaurantMenuApp's own ProductCard uses
// (product.isVeg !== undefined) — items whose source data never carried
// veg/non-veg info render nothing, rather than a misleading dot. One shared
// component so every template's card below reuses the exact same
// markup/classes instead of repeating it.
const VegNonVegBadge = ({ isVeg }) => {
  if (isVeg === undefined) return null;
  return (
    <div className="menu-section__veg-badge-wrap">
      <div className="menu-section__veg-badge">
        <div className={isVeg ? 'menu-section__veg-dot' : 'menu-section__non-veg-dot'}></div>
      </div>
    </div>
  );
};

// TraditionalMenu (T3) is the one template here with no product image at
// all to overlay a badge onto — just the same colored dot inline next to
// the item name instead, since there's no photo corner to anchor to.
const VegNonVegInlineDot = ({ isVeg }) => {
  if (isVeg === undefined) return null;
  return <span className={`menu-section__inline-dot ${isVeg ? 'menu-section__veg-dot' : 'menu-section__non-veg-dot'}`}></span>;
};

const MenuSection = ({ config, nearestStore }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [menuCategories, setMenuCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Tracks which branchId has already been fetched (rather than a plain
  // boolean) so that when branchId resolves asynchronously — it starts
  // undefined and is set later via the nearestStore prop — the corrected
  // fetch for the real branchId isn't silently skipped by a guard that
  // only remembers "a fetch already happened," regardless of which
  // branchId it was for.
  const fetchedForBranchIdRef = useRef(MENU_SECTION_NOT_FETCHED);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
  const X_API_KEY = process.env.REACT_APP_X_API_KEY;
  const X_TENANT = getTenantId();
  const branchId = nearestStore?.id;

  useEffect(() => {
    const fetchMenuData = async () => {
      if (fetchedForBranchIdRef.current === branchId) return;
      const cachedData = apiCache.getMenuPreviewData(branchId);
      if (cachedData) {
        setMenuCategories(cachedData);
        setLoading(false);
        fetchedForBranchIdRef.current = branchId;
        setError(null);
        return;
      }
      try {
        setLoading(true);
        const categoriesUrl = branchId
          ? `${API_BASE_URL}/web/item-category?branch_id=${branchId}`
          : `${API_BASE_URL}/web/item-category`;
        const itemsUrl = branchId
          ? `${API_BASE_URL}/web/item?branch_id=${branchId}`
          : `${API_BASE_URL}/web/item`;

        const commonHeaders = {
          'Content-Type': 'application/json',
          'X-Api-Key': X_API_KEY,
          'X-Tenant': X_TENANT
        };

        const [categoriesRes, itemsRes] = await Promise.all([
          fetch(categoriesUrl, { headers: commonHeaders }),
          fetch(itemsUrl, { headers: commonHeaders })
        ]);

        if (!categoriesRes.ok || !itemsRes.ok) throw new Error(`Failed to fetch menu data`);

        const categoriesData = await categoriesRes.json();
        const itemsData = await itemsRes.json();

        const categories = categoriesData.data || [];
        const items = itemsData.data || [];

        // Only categories that actually have an item for this branch — the
        // category list is tenant-wide, the item list is scoped to the branch's
        // assigned outlet menu (or the master menu), so drop the empty ones
        // BEFORE taking the first 4.
        const filteredCategories = categories
          .filter(cat => cat.id !== 0)
          .filter(cat => items.some(item => item.item_category_id === cat.id))
          .slice(0, 4)
          .map(category => ({
            id: category.id,
            title: category.name,
            icon: 'flaticon-canape',
            name: category.name,
            image: category.image || '',
            items: items
              .filter(item => item.item_category_id === category.id)
              .slice(0, 10)
              .map(item => ({
                name: item.name,
                description: item.description || '',
                price: item.price || '0',
                image: item.thumb || item.cover || config?.logo || '',
                // Same field/derivation RestaurantMenuApp's loadInitialData
                // uses (isVeg: product.item_type === 5) — no new veg/non-veg
                // detection logic.
                isVeg: item.item_type === 5,
                spiceLevel: item.spice_level || 0 // 0-5; 0 shows nothing
              }))
          }));

        apiCache.setMenuPreviewData(branchId, filteredCategories);
        setMenuCategories(filteredCategories);
        setError(null);
      } catch (err) {
        if (config?.menu?.categories) {
          setMenuCategories(config.menu.categories);
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
        fetchedForBranchIdRef.current = branchId;
      }
    };
    fetchMenuData();
  }, [branchId, X_API_KEY, X_TENANT, API_BASE_URL, config]);

  const menu = config?.menu || {
    heading: 'Our Menu',
    sub_heading: 'Discover a variety of delicious dishes made with love and the finest ingredients.'
  };
  const type = (config?.templateType || 'template1').toLowerCase().replace(/-/g, '_');

  if (loading) {
    return (
      <div id="menu" ref={sectionRef} className="menu-main pad-top-100 pad-bottom-100" style={{ minHeight: '600px' }}>
        <div className="container">
          <h2 className="block-title text-center" style={{ color: 'var(--color-primary)' }}>{menu.heading}</h2>
          <p className="title-caption text-center">{menu.sub_heading}</p>
          <div className="modern-testimonials__title-line" />

          <div className="menu-spinner-container">
            <div className="menu-fancy-spinner"></div>
            <h3 className="menu-spinner-text">Preparing the Menu...</h3>

          </div>
        </div>
      </div>
    );
  }

  switch (type) {
    case 'template1':
    case 'modern':
      break;
    case 'template2':
    case 'classic':
      return <ClassicMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template3':
    case 'traditional':
      return <TraditionalMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template4':
    case 'asha_legacy':
      return <AshaLegacyMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template5':
    case 'apparel':
      return <ApparelMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template6':
    case 'luxury_restaurant':
      return <LuxuryMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template7':
    case 'sweet_shop':
      return <SweetShopMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template8':
    case 'heritage_royal':
      return <HeritageMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template9':
    case 'modern_mithai':
      return <ModernMithaiMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template10':
    case 'festive_sweets':
      return <FestiveMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template11':
    case 'royal_confections':
      return <RoyalMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template12':
    case 'traditional_sweets':
      return <TraditionalSweetsMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template13':
    case 'sunburst_gourmet':
      return <SunburstMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template14':
    case 'cafe_premium':
      return <CafeMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template15':
    case 'dry_fruits':
      return <DryFruitsMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    case 'template16':
    case 'arisa_collection':
      return <SweetStoreMenu config={config} menuCategories={menuCategories} loading={loading} activeTab={activeTab} setActiveTab={setActiveTab} />;
    default:
      break;
  }

  const activeCategoryId = menuCategories[activeTab]?.id;

  return (
    <div id="menu" ref={sectionRef} className="menu-main pad-top-100 pad-bottom-100" style={{ minHeight: '800px' }}>
      <div className="container">
        <h2 className="block-title text-center">{menu.heading}</h2>
        <p className="title-caption text-center">{menu.sub_heading}</p>
        <div className="modern-testimonials__title-line" />

        <div className="tab-menu mt-5">
          <div className="slider slider-nav mb-4" style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
            {menuCategories.map((cat, i) => (
              <div key={i} className={`tab-title-menu ${activeTab === i ? 'is-active' : ''}`} onClick={() => setActiveTab(i)}>
                <h2>{cat.title}</h2>
              </div>
            ))}
          </div>
          <div className="row">
            {menuCategories[activeTab]?.items?.map((item, idx) => (
              <div key={idx} className="col-lg-6 col-md-6 mb-4 d-flex align-items-stretch" onClick={() => goToItemCategory(navigate, activeCategoryId)}>
                <MenuItem {...item} />
              </div>
            ))}
          </div>
          <div className="text-center mt-4 mb-5">
            <button className="menu-btn" onClick={() => navigate('/restaurants')}>View More</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// T2: CLASSIC (Fine Dining Elegant List)
const ClassicMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="classic-menu pad-top-120 pad-bottom-120">
      <div className="container">
        {/* Elegant Centered Header */}
        <div className="classic-menu__header text-center">
          <h2 className="classic-menu__title">{menu.heading}</h2>
          <span className="classic-menu__kicker">{menu.sub_heading}</span>
          <div className="modern-testimonials__title-line" />


        </div>

        {/* Minimalist Text Tabs */}
        <div className="classic-menu__tabs">
          {menuCategories.map((cat, i) => (
            <button
              key={i}
              className={`classic-menu__tab-btn ${activeTab === i ? 'active' : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {cat.title}
            </button>
          ))}
        </div>

        {/* Fine Dining List Layout */}
        <div className="classic-menu__list-layout">
          <div className="row">
            {menuCategories[activeTab]?.items?.map((item, idx) => (
              <div key={idx} className="col-lg-6">
                <div className="classic-list-item" onClick={() => goToItemCategory(navigate, activeCategoryId)}>
                  <div className="classic-list-item__img-wrap">
                    <VegNonVegBadge isVeg={item.isVeg} />
                    <img src={item.image} alt={item.name} />
                  </div>
                  <div className="classic-list-item__content">
                    <div className="classic-list-item__header">
                      <h4 className="classic-list-item__name">{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                      <div className="classic-list-item__dots"></div>
                      <span className="classic-list-item__price">₹{item.price}</span>
                    </div>
                    <p className="classic-list-item__desc">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* View All */}
        <div className="text-center" style={{ marginTop: '60px' }}>
          <button className="classic-menu__view-btn" onClick={() => navigate('/restaurants')}>
            Explore Full Menu
          </button>
        </div>
      </div>
    </div>
  );
};

// T3: TRADITIONAL (RIGHT SIDEBAR)
const TraditionalMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="trad-menu-section pad-top-120 pad-bottom-120">
      <div className="container">
        <div className="trad-menu__header text-center mb-5">
          <span className="trad-menu__kicker">{menu.sub_heading}</span>
          <h2 className="trad-menu__title">{menu.heading}</h2>
          <div className="trad-menu__ornament">
            <span className="trad-menu__line"></span>
            <span className="trad-menu__icon">✧</span>
            <span className="trad-menu__line"></span>
          </div>
        </div>

        <div className="trad-menu__tabs mb-5">
          {menuCategories.map((cat, i) => (
            <button
              key={i}
              className={`trad-menu__tab ${activeTab === i ? 'is-active' : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {cat.title}
            </button>
          ))}
        </div>

        <div className="trad-menu__grid">
          <div className="row">
            {menuCategories[activeTab]?.items?.map((item, idx) => (
              <div key={idx} className="col-lg-6 mb-4">
                <div className="trad-menu__item" onClick={() => goToItemCategory(navigate, activeCategoryId)}>
                  <div className="trad-menu__item-main">
                    <h4 className="trad-menu__item-name"><VegNonVegInlineDot isVeg={item.isVeg} />{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                    <div className="trad-menu__item-leader"></div>
                    <div className="trad-menu__item-price">₹{item.price}</div>
                  </div>
                  <p className="trad-menu__item-desc">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const AshaLegacyMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="asha-menu pad-top-120 pad-bottom-0">
      <div className="container">
        <div className="asha-menu__header text-center mb-5">
          <span className="asha-menu__subtitle">{menu.sub_heading}</span>
          <h2 className="asha-menu__title">{menu.heading}</h2>

        </div>
        <div className="asha-menu__tabs-list">
          {menuCategories.map((cat, i) => (
            <div key={i} className={`asha-menu__tab-item ${activeTab === i ? 'is-active' : ''}`} onClick={() => setActiveTab(i)}>

              <span className="asha-tab-text">{cat.title}</span>
            </div>
          ))}
        </div>
        <div className="row mt-5">
          {menuCategories[activeTab]?.items?.map((item, idx) => (
            <div key={idx} className="col-lg-3 col-md-6 mb-4">
              <div className="asha-menu-card">
                <div className="asha-card-inner">
                  <div className="asha-card-front">
                    <VegNonVegBadge isVeg={item.isVeg} />
                    <img src={item.image} alt={item.name} />
                    <div className="asha-card-info"><h4>{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} /><span className="asha-price">₹{item.price}</span></div>
                  </div>
                  <div className="asha-card-back">
                    <h4>{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                    <span className="asha-price" style={{ display: 'block', marginBottom: '10px' }}>₹{item.price}</span>
                    <p>{item.description}</p>
                    <button onClick={() => goToItemCategory(navigate, activeCategoryId)}>Order Now</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ApparelMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="apparel-menu-section pad-top-100 pad-bottom-100">
      <div className="container">
        <div className="text-center mb-5">
          <h2 className="apparel-menu-title">{menu.heading}</h2>
          <p className="apparel-menu-desc">{menu.sub_heading}</p>
        </div>
        <div className="apparel-menu__tabs mb-5">
          {menuCategories.map((cat, i) => (
            <div key={i} className={`apparel-menu__tab ${activeTab === i ? 'is-active' : ''}`} onClick={() => setActiveTab(i)}>
              <h2>{cat.title}</h2>
            </div>
          ))}
        </div>
        <div className="apparel-menu__grid">
          {menuCategories[activeTab]?.items?.map((item, idx) => (
            <div key={idx} className="apparel-menu__card">
              <div className="apparel-menu__card-img">
                <VegNonVegBadge isVeg={item.isVeg} />
                <img src={item.image} alt={item.name} />
                <div className="apparel-menu__overlay">
                  <button className="apparel-menu__view-btn" onClick={() => goToItemCategory(navigate, activeCategoryId)}>Shop Now</button>
                </div>
              </div>
              <div className="apparel-menu__card-info">
                <h4 className="apparel-menu__item-name">{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                <p className="apparel-menu__item-desc text-muted">{item.description.substring(0, 60)}...</p>
                <span className="apparel-menu__item-price">₹{item.price}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const LuxuryMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="lux-menu-section">
      <div className="container">
        {/* Elegant Header */}
        <div className="lux-header text-center mb-5">
          <h2 className="lux-title">{menu.heading}</h2>
          <span className="lux-kicker">{menu.sub_heading}</span>
          <div className="modern-testimonials__title-line" />
          <div className="lux-cats-wrap">
            {menuCategories.map((cat, i) => (
              <button
                key={i}
                className={`lux-cat-pill ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {cat.title}
              </button>
            ))}
          </div>
        </div>

        {/* Elegant Grid List */}
        <div className="lux-menu-grid">
          {menuCategories[activeTab]?.items?.map((item, idx) => (
            <div key={idx} className="lux-menu-card">
              <div className="lux-menu-card__img-box">
                <VegNonVegBadge isVeg={item.isVeg} />
                <img src={item.image} alt={item.name} className="lux-menu-card__img" />
              </div>
              <div className="lux-menu-card__content">
                <h3 className="lux-menu-card__name">{item.name}</h3><SpiceLevel level={item.spiceLevel} size={12} />
                <p className="lux-menu-card__desc">{item.description}</p>
                <div className="lux-menu-card__bottom">
                  <div className="lux-menu-card__price">₹{item.price}</div>
                  <button className="lux-menu-card__btn" onClick={() => goToItemCategory(navigate, activeCategoryId)}>
                    ORDER NOW <i className="fa fa-angle-right ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SweetShopMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="ss-menu">
      <div className="container">
        <div className="ss-menu__header text-center mb-5">
          <span className="ss-menu__subtitle">{menu.sub_heading}</span>
          <h2 className="ss-menu__title">{menu.heading}</h2>
          <div className="ss-menu__ornament">
            <span className="ss-menu__ornament-line"></span><span className="ss-menu__ornament-icon">✦</span><span className="ss-menu__ornament-line"></span>
          </div>
        </div>
        <div className="ss-menu__categories-row mb-5">
          {menuCategories.map((cat, i) => (
            <div key={i} className={`ss-menu__cat-pill ${activeTab === i ? 'is-active' : ''}`} onClick={() => setActiveTab(i)}>
              <span className="ss-menu__cat-label">{cat.title}</span>
            </div>
          ))}
        </div>
        <div className="ss-menu__grid-v2">
          {menuCategories[activeTab]?.items?.map((item, idx) => (
            <div key={idx} className="ss-card-v2">
              <div className="ss-card-v2__img-container">
                <VegNonVegBadge isVeg={item.isVeg} />
                <img src={item.image} alt={item.name} className="ss-card-v2__img" />
              </div>
              <div className="ss-card-v2__details">
                <h4 className="ss-card-v2__name">{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                <div className="ss-card-v2__meta">
                  <span className="ss-card-v2__price">₹{item.price}</span>
                  <span className="ss-card-v2__tag">{menuCategories[activeTab]?.title}</span>
                </div>
                <p className="ss-card-v2__desc">{item.description}</p>
                <button className="ss-card-v2__btn" onClick={() => goToItemCategory(navigate, activeCategoryId)}>ORDER NOW</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const HeritageMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="hr-menu pad-top-100 pad-bottom-100">
      <div className="container">
        <div className="hr-menu__header text-center mb-5">
          <div className="hr-menu__ornament"><span className="hr-ornament-line" /><span className="hr-ornament-icon">✦</span><span className="hr-ornament-line" /></div>
          <h2 className="hr-menu__title">{menu.heading}</h2>
          <p className="hr-menu__desc">{menu.sub_heading}</p>
        </div>
        <div className="hr-menu__tabs mb-5">
          {menuCategories.map((cat, i) => (
            <button key={i} className={`hr-menu__tab ${activeTab === i ? 'is-active' : ''}`} onClick={() => setActiveTab(i)}>{cat.title}</button>
          ))}
        </div>
        <div className="hr-menu__grid">
          {menuCategories[activeTab]?.items?.map((item, idx) => (
            <div key={idx} className="hr-menu__card">
              <div className="hr-menu__card-img"><VegNonVegBadge isVeg={item.isVeg} /><img src={item.image} alt={item.name} /></div>
              <div className="hr-menu__card-info">
                <h4 className="hr-menu__item-name">{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                <p className="hr-menu__item-desc text-muted">{item.description}</p>
                <div className="hr-menu__item-footer">
                  <span className="hr-menu__item-price">₹{item.price}</span>
                  <button className="hr-menu__btn" onClick={() => goToItemCategory(navigate, activeCategoryId)}>Order</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ModernMithaiMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="mm-menu pad-top-100 pad-bottom-100">
      <div className="container">
        <div className="mm-menu__header text-center mb-5">
          <span className="mm-menu__kicker">{menu.sub_heading}</span>
          <h2 className="mm-menu__title">{menu.heading}</h2>
        </div>
        <div className="mm-menu__tabs mb-5">
          {menuCategories.map((cat, i) => (
            <button key={i} className={`mm-menu__tab ${activeTab === i ? 'is-active' : ''}`} onClick={() => setActiveTab(i)}>{cat.title}</button>
          ))}
        </div>
        <div className="mm-menu__grid">
          {menuCategories[activeTab]?.items?.map((item, idx) => (
            <div key={idx} className="mm-menu__card">
              <div className="mm-menu__card-img"><VegNonVegBadge isVeg={item.isVeg} /><img src={item.image} alt={item.name} /></div>
              <div className="mm-menu__card-info">
                <h4 className="mm-menu__item-name">{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                <p className="mm-menu__item-desc text-muted">{item.description}</p>
                <div className="mm-menu__item-footer">
                  <span className="mm-menu__item-price">₹{item.price}</span>
                  <button className="mm-menu__add-btn" onClick={() => goToItemCategory(navigate, activeCategoryId)}>Buy Now</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const FestiveMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="festive-menu-v2 pad-top-120 pad-bottom-120">
      <div className="container">
        <div className="festive-header text-center mb-5">
          <span className="festive-kicker">{menu.sub_heading}</span>
          <h2 className="festive-title">{menu.heading}</h2>
          <div className="festive-divider">
            <span className="festive-divider-line"></span>
            <span className="festive-divider-icon">✦</span>
            <span className="festive-divider-line"></span>
          </div>
        </div>

        <div className="festive-tabs-v2 text-center mb-5">
          <div className="festive-nav-list">
            {menuCategories.map((cat, i) => (
              <button
                key={i}
                className={`festive-nav-pill ${activeTab === i ? 'is-active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                <span className="pill-index">{String(i + 1).padStart(2, '0')}</span>
                <span className="pill-text">{cat.title}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="festive-grid-v2">
          {menuCategories[activeTab]?.items?.map((item, idx) => (
            <div key={idx} className="festive-card-v2" onClick={() => goToItemCategory(navigate, activeCategoryId)}>
              <div className="festive-card-image">
                <VegNonVegBadge isVeg={item.isVeg} />
                <img src={item.image} alt={item.name} />
                <div className="festive-card-price">₹{item.price}</div>
                <div className="festive-card-overlay">
                  <button className="festive-card-btn">Quick Buy</button>
                </div>
              </div>
              <div className="festive-card-body">
                <h4 className="festive-item-name">{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                <p className="festive-item-desc">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const RoyalMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="royal-menu pad-top-100 pad-bottom-100">
      <div className="container">
        <div className="royal-menu__top text-center mb-5">
          <h2 className="royal-menu__heading">{menu.heading}</h2>
          <p className="royal-menu__desc">{menu.sub_heading}</p>
          <div className="royal-menu__pills mt-4">
            {menuCategories.map((cat, i) => (
              <button key={i} className={`royal-pill ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>{cat.title}</button>
            ))}
          </div>
        </div>
        <div className="royal-list">
          {menuCategories[activeTab]?.items?.map((item, idx) => (
            <div key={idx} className="royal-item-row">
              <div className="item-img"><VegNonVegBadge isVeg={item.isVeg} /><img src={item.image} alt={item.name} /></div>
              <div className="item-main">
                <div className="royal-item-header">
                  <h4 className="royal-item-name">{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                  <span className="royal-item-dots" />
                  <span className="royal-item-price">₹{item.price}</span>
                </div>
                <p>{item.description}</p>
              </div>
              <button className="order-btn" onClick={() => goToItemCategory(navigate, activeCategoryId)}>Order</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TraditionalSweetsMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="ts-menu-section pad-top-120 pad-bottom-120">
      <div className="container">
        <div className="ts-menu__header text-center mb-5">
          <h2 className="ts-menu__title">{menu.heading}</h2>
          <p className="ts-menu__desc">{menu.sub_heading}</p>
        </div>
        <div className="row">
          <div className="col-lg-8">
            <div className="row">
              {menuCategories[activeTab]?.items?.map((item, idx) => (
                <div key={idx} className="col-md-6 mb-4">
                  <div className="ts-menu-card" onClick={() => goToItemCategory(navigate, activeCategoryId)}>
                    <div className="img-box"><VegNonVegBadge isVeg={item.isVeg} /><img src={item.image} alt={item.name} /></div>
                    <div className="card-body">
                      <h4 className="ts-item-name">{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                      <p className="ts-item-desc">{item.description.substring(0, 50)}...</p>
                      <span className="ts-item-price">₹{item.price}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-lg-4">
            <div className="ts-sidebar">
              <h3 className="sidebar-title">{menu.sub_heading}</h3>
              <div className="ts-cats mt-4">
                {menuCategories.map((cat, i) => (
                  <div key={i} className={`ts-cat-card ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>
                    <i className="fa fa-circle-o" /> <span>{cat.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SunburstMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="ssb-menu-section pad-top-120 pad-bottom-120">
      <div className="container">
        <div className="row">
          <div className="col-lg-9">
            <div className="ssb-header mb-5"><h2>{menu.heading}</h2><p>{menu.sub_heading}</p></div>
            <div className="row">
              {menuCategories[activeTab]?.items?.map((item, idx) => (
                <div key={idx} className="col-md-6 mb-4">
                  <div className="ssb-menu-card" onClick={() => goToItemCategory(navigate, activeCategoryId)}>
                    <div className="img-box"><VegNonVegBadge isVeg={item.isVeg} /><img src={item.image} alt={item.name} /><div className="price-tag">₹{item.price}</div></div>
                    <div className="info"><h4>{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} /><p>{item.description}</p><button className="add-btn">ADD <i className="fa fa-plus" /></button></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-lg-3">
            <div className="ssb-sidebar sticky-top" style={{ top: '120px' }}>
              <span className="tag">COLLECTIONS</span>
              <div className="ssb-cats mt-3">
                {menuCategories.map((cat, i) => (
                  <div key={i} className={`ssb-cat-item ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}><span className="dot" /> {cat.title}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// T14: CAFÉ PREMIUM MENU
const CafeMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;
  return (
    <div className="cafe-menu pad-top-100 pad-bottom-100">
      <div className="container">
        <div className="cafe-menu__header text-center mb-5">
          <span className="cafe-menu__kicker">{menu.sub_heading}</span>
          <h2 className="cafe-menu__title">{menu.heading}</h2>
          <div className="cafe-menu__ornament">
            <span className="cafe-ornament-line" />
            <span className="cafe-ornament-icon">☕</span>
            <span className="cafe-ornament-line" />
          </div>
        </div>

        <div className="cafe-menu__tabs mb-5">
          {menuCategories.map((cat, i) => (
            <button
              key={i}
              className={`cafe-menu__tab ${activeTab === i ? 'is-active' : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {cat.title}
            </button>
          ))}
        </div>

        <div className="cafe-menu__grid">
          {menuCategories[activeTab]?.items?.map((item, idx) => (
            <div key={idx} className="cafe-menu__card">
              <div className="cafe-menu__card-img">
                <VegNonVegBadge isVeg={item.isVeg} />
                <img src={item.image} alt={item.name} />
                <div className="cafe-menu__card-overlay">
                  <button className="cafe-menu__add-btn" onClick={() => goToItemCategory(navigate, activeCategoryId)}>
                    <i className="fa fa-shopping-bag" style={{ marginRight: '6px' }} /> Add to Cart
                  </button>
                </div>
                <div className="cafe-menu__card-price">₹{item.price}</div>
              </div>
              <div className="cafe-menu__card-info">
                <h4 className="cafe-menu__item-name">{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                <p className="cafe-menu__item-desc">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-5">
          <button className="cafe-menu__view-all" onClick={() => navigate('/restaurants')}>
            View Full Menu <i className="fa fa-arrow-right" style={{ marginLeft: '10px' }} />
          </button>
        </div>
      </div>
    </div>
  );
};

// T15: DRY FRUITS MENU
const DryFruitsMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;

  if (loading) {
    return <div className="text-center py-5">Loading featured products...</div>;
  }

  return (
    <div className="df-menu pad-top-100 pad-bottom-100">
      <div className="container">
        <div className="df-menu__header text-center mb-5">
          <h2 className="df-menu__title">{menu.heading || 'FEATURED PRODUCTS'}</h2>
        </div>

        <div className="df-menu__tabs mb-5 text-center">
          {menuCategories.map((cat, i) => (
            <button
              key={i}
              className={`df-menu__tab ${activeTab === i ? 'is-active' : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {cat.title}
            </button>
          ))}
        </div>

        <div className="df-menu__carousel-wrapper">
          <div className="df-menu__grid">
            {menuCategories[activeTab]?.items?.map((item, idx) => (
              <div key={idx} className="df-menu__card">
                <div className="df-menu__card-img" onClick={() => goToItemCategory(navigate, activeCategoryId)}>
                  <VegNonVegBadge isVeg={item.isVeg} />
                  <img src={item.image} alt={item.name} />
                </div>
                <div className="df-menu__card-info">
                  <h4 className="df-menu__item-name">{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                  <div className="df-menu__item-price">₹{item.price}</div>
                  <button className="df-menu__add-btn" onClick={() => goToItemCategory(navigate, activeCategoryId)}>
                    <i className="fa fa-shopping-cart"></i> Add
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TEMPLATE 16 — ARISA COLLECTION — "Signature Collection"
// Underline tabs + editorial product grid, distinct from the Best
// Sellers grid in SpecialMenu (square hover-reveal cards). Not currently
// included in Template 16's own defaultSections (SpecialMenu's Best
// Sellers is the live "specialities" section there).
// ═══════════════════════════════════════════════════════════════════
const SweetStoreMenu = ({ config, menuCategories, loading, activeTab, setActiveTab }) => {
  const { menu } = config;
  const navigate = useNavigate();
  const activeCategoryId = menuCategories[activeTab]?.id;

  if (loading) {
    return <div className="text-center py-5">Loading our collection...</div>;
  }

  return (
    <div id="menu" className="t16-signature">
      <div className="t16-signature__container">
        <div className="t16-signature__header">
          <span className="t16-signature__kicker">Handpicked For You</span>
          <h2 className="t16-signature__title">{menu?.heading || 'Our Signature Collection'}</h2>
        </div>

        <div className="t16-signature__tabs">
          {menuCategories.map((cat, i) => (
            <button
              key={i}
              className={`t16-signature__tab ${activeTab === i ? 'is-active' : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {cat.title}
            </button>
          ))}
        </div>

        <div className="t16-signature__grid">
          {menuCategories[activeTab]?.items?.map((item, idx) => (
            <div key={idx} className="t16-signature__card" onClick={() => goToItemCategory(navigate, activeCategoryId)}>
              <div className="t16-signature__img-box">
                <VegNonVegBadge isVeg={item.isVeg} />
                <img src={item.image} alt={item.name} />
              </div>
              <div className="t16-signature__info">
                <h4 className="t16-signature__name">{item.name}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                <span className="t16-signature__price">₹{item.price}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MenuSection;
